import React from 'react'
import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material'
import type { GamePhase } from '../types'
import { tvGradientBackground, neonText, TV_COLORS } from '../designSystem'

type GameShellProps = {
  phase: GamePhase
  statusMessage: string
  loading: boolean
  onStart: () => void
  onReset: () => void
  children: React.ReactNode
}

const PHASE_LABELS: Record<GamePhase, string> = {
  idle: 'Sélection',
  loading: 'Chargement',
  qualification: 'Qualification',
  final_ready: 'Super enchère',
  final: 'Super enchère',
  success: 'Victoire',
  fail: 'Terminé',
}

const GameShell = ({
  phase,
  statusMessage,
  loading,
  onStart,
  onReset,
  children,
}: GameShellProps) => {
  const isRunning = phase === 'qualification' || phase === 'final' || phase === 'final_ready'
  const phaseLabel = PHASE_LABELS[phase] ?? phase

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: 520,
        borderRadius: 3,
        p: 2,
        ...tvGradientBackground,
        color: '#fff',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        '&::after': {
          content: '""',
          position: 'absolute',
          inset: 0,
          backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'4\' height=\'4\'%3E%3Crect width=\'1\' height=\'1\' fill=\'rgba(255,255,255,0.04)\'/%3E%3C/svg%3E")',
          opacity: 0.5,
          pointerEvents: 'none',
        },
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        sx={{ position: 'relative', zIndex: 1, mb: 0.3, px: 6 }}
      >
        <Box>
          <Typography
            variant="h4"
            sx={{
              fontFamily: 'Bebas Neue',
              fontSize: 32,
              letterSpacing: 2,
              ...neonText(TV_COLORS.yellow),
            }}
          >
            La Juste Enchère
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: 'rgba(255,255,255,0.8)', fontFamily: 'Montserrat', display: 'block', mt: 0.1, textAlign: 'left' }}
          >
            Phase actuelle : {phaseLabel}
          </Typography>
        </Box>

        <Stack direction="row" spacing={0.8}>
          {!isRunning ? (
            <Button
              variant="contained"
              size="small"
              onClick={onStart}
              disabled={loading || isRunning}
              sx={{
                fontFamily: 'Bebas Neue',
                letterSpacing: 1,
                background: `linear-gradient(120deg, ${TV_COLORS.blue}, ${TV_COLORS.violet})`,
                color: '#fff',
                ...neonText(TV_COLORS.cyan),
              }}
            >
              Démarrer
            </Button>
          ) : null}
          <Button
            variant="outlined"
            size="small"
            onClick={onReset}
            disabled={loading}
            sx={{
              borderColor: 'rgba(255,255,255,0.4)',
              color: '#fff',
              fontFamily: 'Bebas Neue',
              letterSpacing: 0.5,
            }}
          >
            Réinitialiser
          </Button>
        </Stack>
      </Stack>

      {loading ? <CircularProgress size={24} sx={{ alignSelf: 'center', zIndex: 1 }} /> : null}
      <Typography
        variant="body2"
        sx={{ fontFamily: 'Montserrat', textAlign: 'center', zIndex: 1, fontSize: 13 }}
      >
        {statusMessage}
      </Typography>

      <Box />

      <Box sx={{ position: 'relative', zIndex: 1 }}>{children}</Box>
    </Box>
  )
}

export default GameShell
